import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import Navbar from './components/layout/Navbar';
import SideBar from './components/layout/SideBar';
import { SidebarProvider } from './contexts/SidebarContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { useNewGui } from './hooks/useNewGui';
import ErrorIndicator from './components/shared/ErrorIndicator';
import ErrorConsole from './components/shared/ErrorConsole';

import UserTable from './components/infra-users/UserTable';
import UserGenerationPage from './components/infra-users/UserGenerationPage';
import Register from './components/auth/Register';
import Login from './components/auth/Login';
import Dashboard from './components/layout/Dashboard';
import OAuthCallback from './components/auth/OAuthCallback';
import VerifyEmail from './components/auth/VerifyEmail';
import { User } from './types';
import UserProfile from './pages/UserProfile';
import AuditPage from './pages/AuditPage';
import ServerInfoPage from './pages/ServerInfoPage';
import { validateAndRestoreAuth, logoutUser } from './store/authSlice';
import { loadUserPreferences } from './store/preferencesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from './store';
import UniqueUserPage from './components/infra-users/UniqueUserPage';
import { UniqueUserDetails } from './components/infra-users/UniqueUserDetails';
import CreateUser from './components/infra-users/CreateUser';
import LicenseManagementPage from './components/licenses/LicenseManagementPage';
import ExtensionApprovalPage from './components/licenses/ExtensionApprovalPage';
import UserPreferences from './pages/UserPreferences';
import UserNotifications from './pages/UserNotifications';
import LoginActivityReportPage from './pages/LoginActivityReportPage';
import PasswordResetPanel from './components/PasswordResetPanel';
import SystemSettingsPage from './pages/SystemSettingsPage';
import UserManagementPage from './pages/UserManagementPage';
import VncViewerPage from './pages/VncViewerPage';
import HelpPage from './pages/HelpPage';
import CleanupTrackingPage from './pages/CleanupTrackingPage';
import DefinitionsPage from './pages/workspace/DefinitionsPage';
import NfsLocationsPage from './pages/workspace/NfsLocationsPage';
import GroupInstancesPage from './pages/workspace/GroupInstancesPage';
import WorkspaceSessionsPage from './pages/workspace/WorkspaceSessionsPage';

import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import eventBus from './eventBus'

const Settings: React.FC = () => <h1 className="text-2xl font-bold mb-4">Settings Page</h1>;

// AntD v5 token theme mirroring the Tailwind tokens in tailwind.config.js
// (docs/design/01-independent-gui-base.md §3). Only applied when useNewGui()
// is true — old-GUI users get AntD's untouched default theme, so every AntD
// component on a page we haven't otherwise touched (Table, Select, Switch,
// Tabs, Modal, DatePicker, ...) still looks exactly as it does today for them.
const newGuiAntdTheme = {
  token: {
    colorPrimary: '#0B5FD0',
    colorError: '#C0392B',
    colorWarning: '#9A6700',
    colorSuccess: '#1A7F4B',
    colorInfo: '#0B6E8A',
    colorText: '#1B2430',
    colorTextSecondary: '#5A6474',
    colorBorder: '#D8DDE4',
    colorBgContainer: '#FFFFFF',
    colorBgLayout: '#F2F4F7',
    borderRadius: 4,
    fontFamily: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif',
  },
};

const ProtectedRoute: React.FC<{
  element: JSX.Element,
  user: User | null,
  loading: boolean,
  authValidated: boolean,
  requiredRoles?: string[]
}> = ({ element, user, loading, authValidated, requiredRoles }) => {
  // CRITICAL SECURITY: Block ALL rendering until authentication is validated
  if (!authValidated || loading) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
            {authValidated ? 'Loading Application' : 'Validating Authentication'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {authValidated ? 'Initializing user session...' : 'Checking login status...'}
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check roles if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const userRole = user.role;
    if (!userRole || !requiredRoles.includes(userRole)) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="text-red-500 text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to access this page.
              Required roles: {requiredRoles.join(', ')}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Your current role: {userRole || 'None'}
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mr-2"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Dashboard
            </button>
          </div>
        </div>
      );
    }
  }

  return element;
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>(); // Use the typed dispatch
  const [loading, setLoading] = useState(true); // Add loading state to handle asynchronous user initialization
  const [authValidated, setAuthValidated] = useState(false); // Critical: Block rendering until auth is validated
  const user = useSelector((state: RootState) => state.auth.user); // Get user from Redux state
  const useNewGuiTheme = useNewGui();

  const toast = useRef<Toast>(null);

  // Browser tab title — CRA does not substitute REACT_APP_* into index.html.
  // Fallback when unset or blank: "Platform Mgmt App"
  useEffect(() => {
    const configured = (process.env.REACT_APP_PMA_TITLE || '').trim();
    document.title = configured || 'Platform Mgmt App';
  }, []);

  // CRITICAL SECURITY FIX: Block all rendering until authentication is validated
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Validate authentication FIRST (blocks access to protected content)
        await dispatch(validateAndRestoreAuth());
        setAuthValidated(true); // Only set to true after successful validation

        // Load user preferences (will use localStorage if API fails)
        await dispatch(loadUserPreferences());
      } catch (error) {
        console.error('Authentication validation failed:', error);
        setAuthValidated(true); // Still allow app to render, but user will be logged out
      } finally {
        setLoading(false); // Set loading to false once initialization is complete
      }
    };

    initializeApp();
  }, [dispatch]);

  // CRITICAL SECURITY: Periodic authentication check to detect logout in other tabs
  useEffect(() => {
    if (!authValidated || !user) return; // Only run when user is authenticated

    const checkAuthPeriodically = async () => {
      try {
        // Silently validate authentication every 30 seconds
        await dispatch(validateAndRestoreAuth());
      } catch (error) {
        console.log('Periodic auth check failed - user may have logged out elsewhere');
        // User will be logged out automatically by validateAndRestoreAuth
      }
    };

    // Check authentication every 30 seconds
    const intervalId = setInterval(checkAuthPeriodically, 30000);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [authValidated, user, dispatch]);

  // CRITICAL SECURITY: Immediate cross-tab logout invalidation
  useEffect(() => {
    // Listen for localStorage changes (works in all browsers)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'force_logout_timestamp' && e.newValue) {
        console.log('🚨 Immediate logout detected from another tab via localStorage');
        dispatch(logoutUser());
        // Force navigation to login
        window.location.href = '/#/login';
      }
    };

    // Listen for BroadcastChannel messages (modern browsers only)
    let channel: BroadcastChannel | null = null;
    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel('auth_channel');
      channel.onmessage = (event) => {
        if (event.data.type === 'LOGOUT') {
          console.log('🚨 Immediate logout detected from another tab via BroadcastChannel');
          dispatch(logoutUser());
          // Force navigation to login
          window.location.href = '/#/login';
        }
      };
    }

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      channel?.close();
    };
  }, [dispatch]);

    useEffect(() => {
      const handleShowToast = ({ message, severity, summary }: { message: string;  summary?: string; severity?: 'success' | 'info' | 'warn' | 'error'; }) => {
        if (!severity) {
            severity = 'info'; // Default severity
        }
         if (!summary) {
            summary = severity.toUpperCase();
         }
         toast.current?.show({ 
                severity, 
                summary, 
                detail: (
                  <div style={{ whiteSpace: 'pre-line', maxWidth: '600px', wordWrap: 'break-word' }}>
                    {message}
                  </div>
                ), 
                life: 5000 // Increased life to give users more time to read
         });
      };
      eventBus.on('show-toast', handleShowToast);
      return () => {
         eventBus.off('show-toast', handleShowToast);
      };
    }, []);


  return (
    <ConfigProvider theme={useNewGuiTheme ? newGuiAntdTheme : {}}>
    <ThemeProvider>
      <SidebarProvider>
        <Router>
          <Navbar />
          {/* Only show SideBar when user is authenticated */}
          {user && <SideBar />}

          {/* Add a wrapper with full-width */}
          <div className="w-full min-h-screen px-4"> {/* Add this wrapper */}
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/verify-email" element={<VerifyEmail />} />

              <Route path="/dashboard" element={<Dashboard />} />

              {/* Protected routes (only accessible if the user is authenticated) */}
              <Route path="/user-table" element={<ProtectedRoute element={<UserTable />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itoperator', 'itadmin', 'superadmin', 'observer', 'approver']} />} />
              <Route path="/settings" element={<ProtectedRoute element={<Settings />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/user/preferences" element={<ProtectedRoute element={<UserPreferences />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/user/notifications" element={<ProtectedRoute element={<UserNotifications />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/my-profile" element={<ProtectedRoute element={<UserProfile />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/gen-users" element={<ProtectedRoute element={<UserGenerationPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itoperator', 'itadmin', 'superadmin']} />} />
              <Route path="/createuser" element={<ProtectedRoute element={<CreateUser />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/uniqueusers" element={<ProtectedRoute element={<UniqueUserPage />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/licmgmt" element={<ProtectedRoute element={<LicenseManagementPage />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/extension-approvals" element={<ProtectedRoute element={<ExtensionApprovalPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'approver', 'superadmin']} />} />
              <Route path="/audit" element={<ProtectedRoute element={<AuditPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['auditor', 'superadmin']} />} />
              <Route path="/reports/login-activity" element={<ProtectedRoute element={<LoginActivityReportPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itoperator', 'itadmin', 'observer', 'superadmin', 'approver']} />} />
              <Route path="/server-info" element={<ProtectedRoute element={<ServerInfoPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'superadmin']} />} />
              <Route path="/pwdreset" element={<ProtectedRoute element={<PasswordResetPanel />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'superadmin']} />} />
              <Route path="/system-settings" element={<ProtectedRoute element={<SystemSettingsPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['superadmin']} />} />
              <Route path="/user-management" element={<ProtectedRoute element={<UserManagementPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['superadmin']} />} />
              <Route path="/vnc-viewer/:userId" element={<ProtectedRoute element={<VncViewerPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itoperator', 'itadmin', 'superadmin']} />} />
              <Route path="/help" element={<ProtectedRoute element={<HelpPage />} user={user} loading={loading} authValidated={authValidated} />} />
              <Route path="/cleanup-tracking" element={<ProtectedRoute element={<CleanupTrackingPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'superadmin']} />} />
              <Route path="/workspace/definitions" element={<ProtectedRoute element={<DefinitionsPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'superadmin', 'itoperator']} />} />
              <Route path="/workspace/nfs" element={<ProtectedRoute element={<NfsLocationsPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'superadmin', 'itoperator']} />} />
              <Route path="/workspace/groups" element={<ProtectedRoute element={<GroupInstancesPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'superadmin', 'itoperator']} />} />
              <Route path="/workspace/sessions" element={<ProtectedRoute element={<WorkspaceSessionsPage />} user={user} loading={loading} authValidated={authValidated} requiredRoles={['itadmin', 'superadmin', 'itoperator']} />} />
              {/* <Route path="/user/:userId" element={<ProtectedRoute element={<UniqueUserDetails />} user={user} loading={loading} authValidated={authValidated} />} /> */}
              <Route path="/user/:email" element={<ProtectedRoute element={<UniqueUserDetails />} user={user} loading={loading} authValidated={authValidated} />} />


            </Routes>
          </div>
        </Router>
        <Toast ref={toast} position="top-right" />
        <ErrorConsole />
      </SidebarProvider>
    </ThemeProvider>
    </ConfigProvider>
  );
};


export default App;
